import { create } from 'zustand';

/**
 * Open-state for the activity pull-up sheet. Lifted out of TopBar so the sheet
 * can be mounted at a stable App-root location (like the order sheets were)
 * rather than inside the per-page TopBar/IonHeader, where parent re-renders
 * could tear down and recreate the IonModal mid-present and flash it closed.
 * The bell in TopBar calls `open()`; the root-level mount reads `isOpen`.
 *
 * `scrollTargetOrderId` lets a caller (e.g. an order activity toast) open the
 * sheet and request that a specific order card be scrolled into view once it
 * renders. The card that matches clears the target via `clearScrollTarget()`
 * so a later manual open doesn't re-scroll.
 */
interface ActivitySheetState {
  isOpen: boolean;
  scrollTargetOrderId: string | null;
  open: () => void;
  /** Open the sheet and scroll to the given order's card once it loads. */
  openToOrder: (orderId: string) => void;
  /** Set the scroll target without changing open-state — used when a toast
   * arrives while the sheet is already open. */
  setScrollTarget: (orderId: string) => void;
  clearScrollTarget: () => void;
  close: () => void;
}

export const useActivitySheetStore = create<ActivitySheetState>((set) => ({
  isOpen: false,
  scrollTargetOrderId: null,
  open: () => set({ isOpen: true }),
  openToOrder: (orderId) => set({ isOpen: true, scrollTargetOrderId: orderId }),
  setScrollTarget: (orderId) => set({ scrollTargetOrderId: orderId }),
  clearScrollTarget: () => set({ scrollTargetOrderId: null }),
  close: () => set({ isOpen: false, scrollTargetOrderId: null }),
}));
